import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

let toastIdCounter = 0;

const ICONS = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
};

const COLORS = {
  success: {
    bg: 'bg-white border-emerald-400 border-2',
    icon: 'text-emerald-500',
    text: 'text-gray-900',
    close: 'text-gray-400 hover:text-gray-600',
  },
  error: {
    bg: 'bg-white border-red-400 border-2',
    icon: 'text-red-500',
    text: 'text-gray-900',
    close: 'text-gray-400 hover:text-gray-600',
  },
  info: {
    bg: 'bg-white border-blue-400 border-2',
    icon: 'text-blue-500',
    text: 'text-gray-900',
    close: 'text-gray-400 hover:text-gray-600',
  },
};

const Toast = ({ toast, onDismiss }) => {
  const Icon = ICONS[toast.type] || ICONS.info;
  const colors = COLORS[toast.type] || COLORS.info;

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg ${colors.bg} animate-slide-in max-w-sm`}
      role="alert"
    >
      <Icon size={20} className={`${colors.icon} flex-shrink-0`} />
      <p className={`text-sm font-medium ${colors.text} flex-1`}>{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className={`${colors.close} flex-shrink-0 transition-colors`}
      >
        <X size={16} />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'success', duration = 5000) => {
    const id = ++toastIdCounter;
    setToasts(prev => [...prev, { id, message, type }]);
    timersRef.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const toast = useCallback((message, type, duration) => addToast(message, type, duration), [addToast]);
  toast.success = (message, duration) => addToast(message, 'success', duration);
  toast.error = (message, duration) => addToast(message, 'error', duration || 7000);
  toast.info = (message, duration) => addToast(message, 'info', duration);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2" style={{ pointerEvents: 'none' }}>
        {toasts.map(t => (
          <div key={t.id} style={{ pointerEvents: 'auto' }}>
            <Toast toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback if used outside provider
    const noop = (msg) => console.log('Toast:', msg);
    noop.success = noop;
    noop.error = noop;
    noop.info = noop;
    return noop;
  }
  return ctx;
};
